/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  IStoryBlokAnnouncement,
  IStoryBlokAnnouncementBanner,
  IStoryBlokCookieBar,
  IStoryBlokNewsletterSignUpModal,
  IStoryBlokSeo,
} from "apps/website/components/page/api.types";
import {
  IStoryBlokAnnouncementBannerProps,
} from "apps/website/components/storyblok/page/StoryBlokAnnouncementBanner";
import { IHeaderLink } from "apps/website/components/base/Button/Button.map";
import {
  HEADER_LINKS_DEFAULT,
  HEADER_LINKS_BREEDER,
  IHeaderLinks,
  HEADER_LINKS_META,
  HEADER_LINKS_FRESH_FOOD_NEW_USER,
  HEADER_LINKS_FRESH_FEAST_TRIAL,
  HEADER_LINKS_FRESH_FEAST_TRIAL_AC,
  HEADER_LINKS_SIGNUP_FLOW_V2_AC,
  HEADER_LINKS_SUFV2,
  HEADER_LINKS_SCOOP_PLANET,
} from "apps/website/components/page/Header/Header.map";

import { makePageTags } from "../website/head";
import { renderStoryBlokRichText } from "../storyblok/text";
import { ImageMax, getStoryBlokImage } from "../storyblok/image";
import { HeaderScript, ILayoutData } from "../../layouts/layouts.map";

export type AssociatedQueryOverride =
  | { type: "discountCode"; code: string }
  | { type: "queryString"; params: Record<string, string> };

export const DEFAULT_DISCOUNT_CODE = "TRY20";

/**
 * Resolves a query override into a formatted query string.
 * Falls back to `discount_code=TRY20` when no override is provided.
 */
export const resolveAssociatedQuery = (
  override?: AssociatedQueryOverride,
  fallbackDiscountCode: string = DEFAULT_DISCOUNT_CODE,
): string => {
  if (!override) {
    return new URLSearchParams({ discount_code: fallbackDiscountCode }).toString();
  }

  if (override.type === "discountCode") {
    return new URLSearchParams({ discount_code: override.code }).toString();
  }

  return new URLSearchParams(override.params).toString();
};

export const getHeadData = (
  seo: IStoryBlokSeo | undefined,
  hideFromSearch: boolean | undefined,
  pageLastBuilt: string | undefined,
  fallbackTags: IStoryBlokSeo | undefined,
) => ({
  seo: seo ? makePageTags(seo) : undefined,
  hideFromSearch: hideFromSearch || false,
  lastBuilt: pageLastBuilt,
  fallbackTags: fallbackTags ? makePageTags(fallbackTags) : undefined,
});

export const getAnnouncementBannerData = (
  pageAnnouncements: IStoryBlokAnnouncement[],
  defaultAnnouncements: IStoryBlokAnnouncementBanner,
): IStoryBlokAnnouncementBannerProps => ({
  pageAnnouncements,
  defaultAnnouncements,
});

export const getCookieBannerData = (
  banner: IStoryBlokCookieBar,
) => ({
  body: renderStoryBlokRichText(banner.body),
  cta: { id: banner.cta[0]._uid, ...banner.cta[0] },
});

export const getNewsletterData = (
  newsletter: IStoryBlokNewsletterSignUpModal,
) => ({
  title: newsletter.title,
  completeTitle: newsletter.complete_title,
  body: newsletter.body,
  completeBody: newsletter.complete_body,
  buttonText: newsletter.button_text,
  theme: newsletter.theme,
  image: getStoryBlokImage(newsletter.image, true, ImageMax.CONTAINER_SM),
});

const mapLinksWithAssociatedDiscount = (links: IHeaderLink[], associatedDiscount: string) => links.map((link) => ({
  ...link,
  to: link.withAssociatedDiscount ? `${link.to}?${associatedDiscount}` : link.to,
}));

const getHeaderLinksWithAssociatedDiscount = (headerLinks: IHeaderLinks, associatedDiscount: string | undefined) => {
  if (!associatedDiscount) { return headerLinks; }
  return {
    primaryLinks: mapLinksWithAssociatedDiscount(headerLinks.primaryLinks, associatedDiscount),
    secondaryLinks: mapLinksWithAssociatedDiscount(headerLinks.secondaryLinks, associatedDiscount),
    authLinks: mapLinksWithAssociatedDiscount(headerLinks.authLinks, associatedDiscount),
    guestLinks: mapLinksWithAssociatedDiscount(headerLinks.guestLinks, associatedDiscount),
  };
};

const getHeaderLinksForStory = (story: any, associatedDiscount: string | undefined) => {
  if (story?.isFreshFoodNewUserPage) {
    return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_FRESH_FOOD_NEW_USER, associatedDiscount);
  }
  if (story?.isFreshFeastTrialPage) {
    return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_FRESH_FEAST_TRIAL, associatedDiscount);
  }
  if (story?.isFreshFeastTrialACPage) {
    return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_FRESH_FEAST_TRIAL_AC, associatedDiscount);
  }
  if (story?.isSignupFlowV2AC) {
    return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_SIGNUP_FLOW_V2_AC, associatedDiscount);
  }
  if (story?.hasSUFv2CTAs) {
    return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_SUFV2, associatedDiscount);
  }

  if (story?.isMetaSignUpFunnelPage) {
    return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_META, associatedDiscount);
  }

  if (story?.main?.content?.header_navigation === "breeder") {
    return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_BREEDER, associatedDiscount);
  }

  if (story?.main?.content?.header_navigation === "scoop-planet") {
    return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_SCOOP_PLANET, associatedDiscount);
  }

  return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_DEFAULT, associatedDiscount);
};

const getAnnouncementBannerIfEnabled = (story: any) => {
  if (!story?.main?.content?.announcements && !story?.announcementBanner) {
    return null;
  }
  return getAnnouncementBannerData(story.main?.content?.announcements, story.announcementBanner);
};

// @ts-ignore
export const getDefaultLayoutData = (
  story?: any,
  lastBuilt?: string,
  scripts: HeaderScript[] = [ "trustpilot" ],
  pageSlug?: string,
  queryOverrides?: Record<string, AssociatedQueryOverride>,
): ILayoutData => {
  const storyblokDiscount = story?.main?.content?.associated_discount_code;
  let formattedAssociatedDiscount: string | undefined;

  if (storyblokDiscount) {
    formattedAssociatedDiscount = new URLSearchParams({ discount_code: storyblokDiscount }).toString();
  } else if (pageSlug && queryOverrides?.[pageSlug]) {
    formattedAssociatedDiscount = resolveAssociatedQuery(queryOverrides[pageSlug]);
  } else if (story?.hasSUFv2CTAs || story?.isFreshFoodNewUserPage) {
    formattedAssociatedDiscount = resolveAssociatedQuery();
  }

  return {
    headerLinks: getHeaderLinksForStory(story, formattedAssociatedDiscount),
    head: story?.main && getHeadData(
      story.main.content.seo,
      story.main.content.hide_from_search_engines,
      lastBuilt,
      story.fallbackTags,
    ),
    scripts,
    announcementBanner: getAnnouncementBannerIfEnabled(story),
    cookieBanner: story?.cookieBar && getCookieBannerData(story.cookieBar),
    hideCookieBanner: story?.main?.content?.hide_cookie_bar,
    hideAnnouncementBanner: story?.main?.content?.hide_announcements || false,
    newsletterModal: story?.newsletterSignUpModal && getNewsletterData(story.newsletterSignUpModal),
    lastBuilt,
    jsonLd: story?.jsonLd,
    isMetaSignUpFunnelPage: story?.isMetaSignUpFunnelPage,
    isFreshFoodNewUserPage: story?.isFreshFoodNewUserPage,
    isFreshFeastTrialPage: story?.isFreshFeastTrialPage,
    isFreshFeastTrialACPage: story?.isFreshFeastTrialACPage,
    isSignupFlowV2AC: story?.isSignupFlowV2AC,
    hasSUFv2CTAs: story?.hasSUFv2CTAs,
    associatedDiscount: formattedAssociatedDiscount,
  };
};
